// Uyarlanmış kopya — emsal: beltasapp/frontend/src/shared/ui/kalemler/grid-cell.tsx (GridCellEditor) + hucre-secim.tsx
// Fark: Base UI/shadcn Input yerine yalın input; liste `document.body` portalı (sticky hücre yığın
// bağlamı tuzağı, Popover yok); arama/tarih hücresi yok; liste öğeleri `GridOption`.
import { useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'

import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

import { mapEditorKey } from './keys'
import type { EditorNav, GridOption } from './types'

/** `text` null = cancelled. `refocus` false = the input lost focus by itself (click elsewhere). */
export type EditorDone = (text: string | null, nav: EditorNav, refocus?: boolean) => void

const t = messages.editableGrid

const INPUT_CLASS =
  'h-full w-full min-w-0 bg-transparent text-xs text-foreground outline-none select-text'

/** Runs `done` at most once: a key may close the editor and the blur that follows must not repeat it. */
function useOnce(done: EditorDone): EditorDone {
  const finished = useRef(false)
  return (text, nav, refocus) => {
    if (finished.current) return
    finished.current = true
    done(text, nav, refocus)
  }
}

export function TextEditor({
  initial,
  numeric,
  ariaLabel,
  onDone,
}: {
  initial: string
  numeric?: boolean
  ariaLabel: string
  onDone: EditorDone
}) {
  const [value, setValue] = useState(initial)
  const ref = useRef<HTMLInputElement>(null)
  const done = useOnce(onDone)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
  }, [])

  return (
    <input
      ref={ref}
      type="text"
      inputMode={numeric ? 'decimal' : undefined}
      aria-label={ariaLabel}
      className={cn(INPUT_CLASS, numeric && 'tabular text-right')}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        mapEditorKey(e, (commit, nav) => done(commit ? value : null, nav))
      }}
      onBlur={() => done(value, 'none', false)}
    />
  )
}

interface ListPlace {
  left: number
  minWidth: number
  top?: number
  bottom?: number
  maxHeight: number
}

export function SelectEditor({
  options,
  current,
  seed,
  ariaLabel,
  onDone,
  onPicked,
}: {
  options: GridOption[]
  /** Stored value of the cell. */
  current: string
  /** Typed character that opened the editor: starts as a filter query. */
  seed?: string
  ariaLabel: string
  onDone: EditorDone
  /** A list pick happened (the caller arms the ghost-click lock). */
  onPicked: () => void
}) {
  const listId = useId()
  const currentLabel = options.find((o) => o.value === current)?.label ?? ''
  const [query, setQuery] = useState(seed ?? currentLabel)
  // Until the user types, the input only shows the current label: no filter yet.
  const [touched, setTouched] = useState(seed !== undefined)
  const [open, setOpen] = useState(true)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [place, setPlace] = useState<ListPlace | null>(null)
  const done = useOnce(onDone)

  const items = useMemo<GridOption[]>(() => {
    const needle = touched ? query.trim().toLocaleLowerCase('tr') : ''
    if (!needle) return [{ value: '', label: t.emptyOption }, ...options]
    return options.filter((o) => o.label.toLocaleLowerCase('tr').includes(needle))
  }, [options, query, touched])

  const [highlight, setHighlight] = useState(() =>
    seed !== undefined ? 0 : Math.max(0, options.findIndex((o) => o.value === current) + 1),
  )
  const active = Math.min(highlight, Math.max(items.length - 1, 0))

  useLayoutEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.focus()
    if (seed === undefined) el.select()
    else el.setSelectionRange(el.value.length, el.value.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function reposition() {
    const el = inputRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const margin = 8
    const below = window.innerHeight - r.bottom - margin
    const above = r.top - margin
    const up = below < 160 && above > below
    setPlace({
      left: r.left,
      minWidth: Math.max(r.width, 200),
      ...(up
        ? { bottom: window.innerHeight - r.top + 2, maxHeight: above }
        : { top: r.bottom + 2, maxHeight: below }),
    })
  }

  useLayoutEffect(() => {
    if (!open) return
    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, query])

  useLayoutEffect(() => {
    const el = listRef.current?.children[active] as HTMLElement | undefined
    el?.scrollIntoView?.({ block: 'nearest' })
  }, [active, open, place])

  const pick = (item: GridOption | undefined, nav: EditorNav) => {
    if (!item) return done(null, nav)
    onPicked()
    done(item.value, nav)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' && !e.altKey) {
      e.preventDefault()
      if (!open) setOpen(true)
      else setHighlight(Math.min(active + 1, items.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(Math.max(active - 1, 0))
    } else if (e.key === 'F4' || (e.altKey && e.key === 'ArrowDown')) {
      e.preventDefault()
      setOpen((o) => !o)
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      const nav: EditorNav =
        e.key === 'Enter' ? (e.shiftKey ? 'up' : 'down') : e.shiftKey ? 'prev' : 'next'
      // Enter with nothing to pick (filter matches nothing) keeps the editor open.
      if (e.key === 'Enter' && items[active] === undefined) return
      pick(items[active], nav)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      done(null, 'none')
    }
  }

  const style: CSSProperties | undefined = place
    ? {
        position: 'fixed',
        left: place.left,
        top: place.top,
        bottom: place.bottom,
        minWidth: place.minWidth,
        maxHeight: place.maxHeight,
        zIndex: 200,
      }
    : undefined

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open ? `${listId}-${active}` : undefined}
        placeholder={t.listPlaceholder}
        className={INPUT_CLASS}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setTouched(true)
          setOpen(true)
          setHighlight(0)
        }}
        onKeyDown={onKeyDown}
        onBlur={() => done(null, 'none', false)}
      />
      {/*
        PORTAL ŞART: sticky + z-index'li hücre kendi yığın bağlamını yaratır, içindeki `fixed` liste
        dışarı çıkamaz ve sonraki satırların arkasında kalır. Base UI Popover da kullanılmaz: hücre
        tıklaması aynı pointer'la `outsidePress` sayılıp listeyi kapatır (beltasapp grid-cells.md).
      */}
      {open &&
        place &&
        createPortal(
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            style={style}
            // preventDefault: the click must not take the focus from the input (blur = cancel).
            // stopPropagation: React bubbles portal events through the React tree up to the cell.
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            className="border-border bg-popover text-popover-foreground shadow-raised overflow-auto rounded-lg border py-1 text-xs"
          >
            {items.length === 0 && (
              <div className="text-muted-foreground px-3 py-2">{t.noResults}</div>
            )}
            {items.map((item, i) => (
              <div
                key={`${item.value}-${i}`}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  pick(item, 'none')
                }}
                onMouseEnter={() => setHighlight(i)}
                className={cn(
                  'cursor-default px-3 py-2 text-left',
                  i === active ? 'bg-surface-muted' : undefined,
                )}
              >
                {item.label}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  )
}
